import { Component } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MdbModalRef } from 'mdb-angular-ui-kit/modal';
import {
  ProductionEquipment,
  ProductionLine,
  ProductionSpeciality,
} from 'src/app/models/interfaces.model';
import { GenericCrudService } from 'src/app/services/crud/generic-crud.service';
import { environment } from 'src/app/environments/environments';
import { firstValueFrom } from 'rxjs';
import * as moment from 'moment';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-modal-see-binnacle',
  templateUrl: './modal-see-binnacle.component.html',
  styleUrls: ['./modal-see-binnacle.component.css'],
})
export class ModalSeeBinnacleComponent {
  requestForm: FormGroup;
  loadingFull: boolean = true;
  loading: any;
  created_at: string = '';
  requesterUser: string = '';
  photo: File | null = null;
  problem: string | null = null;
  id: string = '';
  imagePreview: string | null = null;
  action: string = '';
  visibilityApproveAndReject: boolean = false;
  visibilityRemoveImage: boolean = false;
  visibilityImageInput: boolean = false;
  visibilityModify: boolean = false;
  private focusInterval: any;

  productionLineSelected: string | null = null;
  productionEquipmentSelected: string | null = null;
  productionSpecialitySelected: string | null = null;

  productionLine: ProductionLine[] = [];
  productionEquipment: ProductionEquipment[] = [];
  productionSpeciality: ProductionSpeciality[] = [];

  constructor(
    private fb: FormBuilder,
    public thisModalRef: MdbModalRef<ModalSeeBinnacleComponent> | null = null,
    private crudService: GenericCrudService<any>
  ) {
    this.requestForm = this.fb.group({
      productionLineSelected: [null],
      productionEquipmentSelected: [null],
      productionSpecialitySelected: [null],
      problem: [null],
      photo: new FormControl(null),
    });
  }

  async ngOnInit(): Promise<void> {
    try {
      // this.requestForm.get('productionEquipmentSelected')?.disable();
      this.loadingFull = true;
      await Promise.all([
        this.getRequesterUser(),
        this.getProductionLine(),
        this.getProductionSpeciality(),
        this.loadDataInForm(),
        this.visibilityButtonsByAction(this.action),
        this.disabledFormByAction(this.action),
      ]);
    } catch (error) {
      console.log('[ngOnInit] error: ', error);
    } finally {
      this.loadingFull = false;
    }
  }

  visibilityButtonsByAction(action: string): void {
    if (action === 'approveOrReject') {
      this.visibilityApproveAndReject = true;
    } else if (action === 'edit') {
      this.visibilityRemoveImage = true;
      this.visibilityImageInput = true;
      this.visibilityModify = true;
    }
  }

  disabledFormByAction(action: string): void {
    switch (action) {
      case 'see':
        console.log('[disabledFormByAction] see');
        this.requestForm.get('productionLineSelected')?.disable();
        this.requestForm.get('productionEquipmentSelected')?.disable();
        this.requestForm.get('productionSpecialitySelected')?.disable();
        this.requestForm.get('problem')?.disable();
        this.requestForm.get('photo')?.disable();
        break;
      case 'edit':
        console.log('[disabledFormByAction] edit');
        this.requestForm.get('productionLineSelected')?.enable();
        this.requestForm.get('productionEquipmentSelected')?.enable();
        this.requestForm.get('productionSpecialitySelected')?.enable();
        this.requestForm.get('problem')?.enable();
        this.requestForm.get('photo')?.enable();
        break;
      case 'approveOrReject':
        console.log('[disabledFormByAction] approveOrReject');
        this.requestForm.get('productionLineSelected')?.disable();
        this.requestForm.get('productionEquipmentSelected')?.disable();
        this.requestForm.get('productionSpecialitySelected')?.disable();
        this.requestForm.get('problem')?.disable();
        this.requestForm.get('photo')?.disable();
        break;
      default:
        break;
    }
  }

  async getRequesterUser(): Promise<void> {
    const idUser = localStorage.getItem('IdUser') ?? '';
    const body = new FormData();
    body.append('action', 'SELECT');
    body.append('table_name', 'USERS');
    body.append('id', idUser);
    try {
      const response: any = await firstValueFrom(
        this.crudService.crud(body, environment.GenericCrudOperation)
      );
      if (response && response.status === 1 && response.data) {
        this.requesterUser = response.data[0].SAMACCOUNTNAME;
      }
    } catch (error) {
      console.error('Error: ', error);
    }
  }

  async getProductionSpeciality(): Promise<void> {
    const body = new FormData();
    body.append('action', 'SELECT');
    body.append('table_name', 'PRODUCTION_SPECIALITY');

    try {
      const response: any = await firstValueFrom(
        this.crudService.crud(body, environment.GenericCrudOperation)
      );
      if (response && response.status === 1 && response.data) {
        this.productionSpeciality = response.data;
      }
    } catch (error) {
      console.error('Error: ', error);
    }
  }

  async getProductionLine(): Promise<void> {
    const body = new FormData();
    body.append('action', 'SELECT');
    body.append('table_name', 'PRODUCTION_LINE');

    try {
      const response: any = await firstValueFrom(
        this.crudService.crud(body, environment.GenericCrudOperation)
      );
      if (response && response.status === 1 && response.data) {
        this.productionLine = response.data;
      }
    } catch (error) {
      console.error('Error: ', error);
    }
  }

  async getProductionEquipment(id_production_line: any): Promise<boolean> {
    const body = new FormData();
    body.append('action', 'SELECT');
    body.append('table_name', 'PRODUCTION_EQUIPMENT');
    body.append('id_production_line', id_production_line);

    try {
      const response: any = await firstValueFrom(
        this.crudService.crud(body, environment.GetProductionEquipments)
      );
      if (response && response.status === 1 && response.data) {
        this.productionEquipment = response.data;
      }
      return true;
    } catch (error) {
      return false;
    }
  }

  async buildProductionEquipment(id_production_line: any): Promise<void> {
    try {
      this.loadingFull = true;
      this.requestForm.get('productionEquipmentSelected')?.disable();
      if (
        (await this.getProductionEquipment(id_production_line)) &&
        this.action === 'edit'
      ) {
        this.requestForm.get('productionEquipmentSelected')?.enable();
      }
    } catch (error) {
      console.log('error: ', error);
    } finally {
      this.loadingFull = false;
    }
  }

  async getBinnacleById(id: string): Promise<any> {
    const body = new FormData();
    body.append('action', 'SELECT');
    body.append('table_name', 'BINNACLE');
    body.append('id', id);
    try {
      return await firstValueFrom(
        this.crudService.crud(body, environment.GenericCrudOperation)
      );
    } catch (error) {
      console.error('Error: ', error);
    }
  }

  async loadDataInForm(): Promise<void> {
    const requestBinnacle = await this.getBinnacleById(this.id);
    const register = requestBinnacle.data[0];
    this.requestForm.patchValue({
      productionLineSelected: register.ID_PRODUCTION_LINE,
      productionEquipmentSelected: register.ID_PRODUCTION_EQUIPMENT,
      productionSpecialitySelected: register.ID_PRODUCTION_SPECIALITY,
      problem: register.PROBLEM,
      photo: register.PHOTO,
    });
    await this.buildProductionEquipment(register.ID_PRODUCTION_LINE);
    this.created_at = moment(register.CREATED_AT).format('DD-MM-YYYY');
    console.log('register.PHOTO: ', register.PHOTO);
    this.imagePreview = register.PHOTO;
    console.log('this.imagePreview: ', this.imagePreview);
  }

  async modifyRegister(): Promise<void> {
    Swal.fire({
      title:
        'Estás a punto de actualizar la solicitud #' +
        this.id +
        ', ¿Está seguro de realizar esta acción?',
      icon: 'warning',
      width: 800,
      customClass: {
        popup: 'custom-swal-popup',
      },
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Sí',
      cancelButtonText: 'Cancelar',
      allowOutsideClick: false,
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire({
          title: 'Procesando...',
          allowOutsideClick: false,
          showCancelButton: false,
          showCloseButton: false,
          showConfirmButton: false,
          willOpen: async () => {
            Swal.showLoading();
            const body = new FormData();
            body.append('action', 'UPDATE');
            body.append('table_name', 'BINNACLE');
            body.append('id', this.id);
            body.append(
              'id_production_line',
              this.requestForm.get('productionLineSelected')?.value
            );
            body.append(
              'id_production_equipment',
              this.requestForm.get('productionEquipmentSelected')?.value
            );
            body.append(
              'id_production_speciality',
              this.requestForm.get('productionSpecialitySelected')?.value
            );
            body.append('problem', this.requestForm.get('problem')?.value);
            if (this.photo) {
              console.log('modified photo: ', this.photo);
              body.append('photo', this.photo);
            }
            try {
              const response: any = await firstValueFrom(
                this.crudService.crud(body, environment.GenericCrudOperation)
              );
              if (response && response.status === 1) {
                console.log('response: ', response);
                this.closeModal();
              }
            } catch (error) {
              console.error('Error: ', error);
            }
            Swal.fire({
              icon: 'success',
              title: 'Se ha actualizado correctamente!',
              showConfirmButton: false,
              timer: 1500,
            });
          },
        });
      }
    });
  }

  approveOrReject(status: boolean): void {
    try {
      const action = status ? 'aprobar' : 'rechazar';
      const statusValue = status ? '2' : '3';
      const successMessage = status
        ? 'Se ha aprobado la solicitud!'
        : 'Se ha rechazado la solicitud!';

      Swal.fire({
        title: `¿Está seguro de ${action} la solicitud?`,
        icon: 'warning',
        width: 800,
        customClass: {
          popup: 'custom-swal-popup',
        },
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Sí',
        cancelButtonText: 'Cancelar',
        allowOutsideClick: false,
        input: status ? undefined : 'textarea',
        inputPlaceholder: 'Ingrese el motivo de rechazo...',
        preConfirm: (value) => {
          if (!status && !value) {
            Swal.showValidationMessage(
              'El motivo es obligatorio al rechazar la solicitud.'
            );
          }
          return value;
        },
        didOpen: () => {
          const input = Swal.getInput();
          if (input) {
            input.addEventListener('blur', () => {
              input.focus();
            });
          }
        }
      }).then(async (result) => {
        if (result.isConfirmed) {
          Swal.fire({
            title: 'Procesando...',
            allowOutsideClick: false,
            showCancelButton: false,
            showCloseButton: false,
            showConfirmButton: false,
            willOpen: async () => {
              Swal.showLoading();
              const body = new FormData();
              body.append('action', 'UPDATE');
              body.append('table_name', 'BINNACLE');
              body.append('id', this.id);
              body.append('status', statusValue);

              if (!status) {
                body.append('reject_reason', result.value || ''); // Agregar descripción al rechazar
              }

              const response: any = await firstValueFrom(
                this.crudService.crud(body, environment.GenericCrudOperation)
              );
              if (response && response.status === 1) {
                const bodyMail = new FormData();
                bodyMail.append('id_binnacle', this.id);
                bodyMail.append('id_user', localStorage.getItem('IdUser') ?? '');
                await firstValueFrom(
                  this.crudService.crud(bodyMail, environment.SendMailApproveOrReject)
                );
                Swal.fire({
                  icon: 'success',
                  title: successMessage,
                  showConfirmButton: false,
                  timer: 1500,
                });
                this.closeModal();
              }
            },
          });
        }
      });
    } catch (error) {
      console.error('Error: ', error);
    }
  }

  

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      const file = input.files[0];
      this.photo = file;
      this.requestForm.get('photo')?.setValue(this.photo);

      // Crear una URL para previsualizar la imagen
      const reader = new FileReader();
      reader.onload = () => {
        this.imagePreview = reader.result as string;
      };
      reader.readAsDataURL(file);
    }
  }

  removeImage(): void {
    this.imagePreview = null;
    this.requestForm.get('photo')?.setValue(null);
    const photo = document.getElementById('photo') as HTMLInputElement;
    if (photo) {
      photo.value = ''; // Limpia el input de archivo
    }
  }

  onFileRemoved(): void {
    this.photo = null;
    this.requestForm.get('photo')?.setValue(null);
  }

  closeModal() {
    this.thisModalRef?.close(true);
  }
}
